/**
 * What was on screen when a message was sent (RFC 0304 3d, S3).
 *
 * The agent answers from the thread, and the person talks from the space: a
 * document beside the thread, a review, a terminal. "Change this" names
 * something the agent cannot see. So a send stamps the panes onto the message,
 * and the daemon serves them to the turn as a `<view>` block beside the
 * holdings. It is context for the run, never text in the message, so the
 * transcript does not draw it.
 *
 * PURE, over the window as `mod.window` reads it, so the thread and the strip
 * build the same manifest by construction and the rule is testable without a
 * rendered window.
 */

import type { AttachmentRef, MessageView, ViewPane } from '@mod/core/model'
import type { LeafView } from '../../sdk/window'

/** One pane as `mod.window` reads it: its content and whether it has focus.
 *  Through the SDK's type, since a feature may not import another feature. */
export type ViewLeaf = Pick<LeafView, 'content' | 'focused'>
type Content = ViewLeaf['content']

/** One send-time snapshot: semantic pane identity plus the pane pictures the
 * page drew. */
export interface MessageViewContext {
  view: MessageView | null
  viewAttachments: AttachmentRef[]
}

function paneOf(
  content: Content,
  focused: boolean,
  titleOf: (kind: 'document' | 'session', id: string) => string | undefined,
): ViewPane {
  if (content.kind === 'picker') return { kind: 'empty', focused }
  if (content.kind === 'discover') return { kind: 'empty', focused }
  if (content.kind === 'terminal')
    return content.terminalId
      ? { kind: 'terminal', id: content.terminalId, focused }
      : { kind: 'terminal', focused }
  if (content.kind === 'agent') {
    if (!content.sessionId) return { kind: 'session-list', focused }
    const title = titleOf('session', content.sessionId)
    return {
      kind: 'session',
      id: content.sessionId,
      ...(title ? { title } : {}),
      focused,
    }
  }
  const title = titleOf('document', content.docId)
  return {
    kind: 'document',
    id: content.docId,
    ...(title ? { title } : {}),
    focused,
  }
}

/**
 * The view to stamp, or null when the screen adds nothing.
 *
 * NOTHING TO ADD is the space showing only the thread the message goes to:
 * the RFC's "a space that shows more than the thread". Stamping it anyway
 * would write a field on every message sent from a plain thread.
 */
export function viewManifest(
  leaves: readonly ViewLeaf[],
  opts: {
    /** The session the message goes to, so its own thread is recognised. */
    sessionId: string | null
    space?: string | null
    titleOf: (kind: 'document' | 'session', id: string) => string | undefined
  },
): MessageView | null {
  const panes = leaves.map((l) => paneOf(l.content, l.focused, opts.titleOf))
  const beyondThread = panes.some(
    (p) => !(p.kind === 'session' && p.id === opts.sessionId),
  )
  if (!beyondThread) return null
  return { panes, ...(opts.space ? { space: opts.space } : {}) }
}
